import { Box, Typography } from "@mui/material";
import { GridRowModel } from "@mui/x-data-grid-pro";
import { FieldInputProps } from "formik";
import { FormattedMessage } from "react-intl";
import { useRecoilValue } from "recoil";

import { PreviewsBlockFormDetails } from "../interfaces";
import { GenericObject } from "../../services/Price";
import { formatterService } from "../../NumberFormatter";
import AppAlert from "../../components/AppAlert";

const PreviewBlock = ({
  fieldsOptions,
  field,
  row,
}: {
  fieldsOptions: PreviewsBlockFormDetails;
  field: FieldInputProps<any>;
  row: GridRowModel;
}) => {
  const formatter = useRecoilValue(formatterService);

  const valuesByKey: Record<string, any> = field.value.datasPreview.reduce(
    (acc: Record<string, any>, info: GenericObject) => {
      acc[info.key] = info.value;
      return acc;
    },
    {}
  );

  const narrativePlaceholders = field.value.datasPreview.reduce(
    (acc: Record<string, any>, info: GenericObject) => {
      if (info.value === null || info.value === undefined) {
        acc[info.key] = info.value;
        return acc;
      }
      if (info.inputType === "currency" || info.inputType === "percent") {
        acc[info.key] = formatter.format(Number(info.value), {
          style: info.inputType,
        });
      } else {
        acc[info.key] = info.value;
      }
      return acc;
    },
    {}
  );

  const showWarning = fieldsOptions.warningKey && field.value?.value === false;

  const missingKeys = fieldsOptions.narrativeFallbackOnMissingKeys ?? [];
  const useFallbackNarrative =
    fieldsOptions.narrativeFallbackKey &&
    missingKeys.some(
      (k) => valuesByKey[k] === null || valuesByKey[k] === undefined
    );
  const narrativeKey = useFallbackNarrative
    ? fieldsOptions.narrativeFallbackKey
    : fieldsOptions.narrativeKey;

  return (
    <Box
      sx={(theme) => ({
        border: `2px solid ${theme.vars.palette.divider}`,
        backgroundColor: "background.sunken",
        padding: 1,
      })}
    >
      <>
        <Typography
          component={"p"}
          sx={{ textAlign: "center", marginBottom: 2.5 }}
        >
          <FormattedMessage id={fieldsOptions.label} />
        </Typography>
        {showWarning && (
          <AppAlert severity="warning" sx={{ marginBottom: 2 }}>
            <FormattedMessage id={fieldsOptions.warningKey} />
          </AppAlert>
        )}
        {narrativeKey && (
          <Typography component={"p"} sx={{ marginBottom: 2, lineHeight: 1.6 }}>
            <FormattedMessage
              id={narrativeKey}
              values={narrativePlaceholders}
            />
          </Typography>
        )}
        <Box sx={{ display: "flex", flexWrap: "wrap" }}>
          {field.value.datasPreview.map((info: GenericObject) => {
            let formattedValue;
            if (
              (info.inputType === "currency" || info.inputType === "percent") &&
              (info.value || info.value === 0)
            ) {
              formattedValue = formatter.format(Number(info.value), {
                style: info.inputType,
              });
            }
            return (
              <Box
                key={info.label}
                sx={{
                  width: "calc(50% - 16px)",
                  mx: 1,
                  display: "flex",
                }}
              >
                <Typography component={"span"}>
                  <FormattedMessage id={info.label} />
                </Typography>
                {(info.value || info.value === 0) && (
                  <Box
                    component="span"
                    sx={{
                      borderBottom: "0px dotted",
                      flexGrow: 1,
                    }}
                  />
                )}
                <Typography component={"span"}>
                  {formattedValue || info.value}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </>
    </Box>
  );
};

export default PreviewBlock;
